import { useCallback, useEffect, useState } from "react";
import { Gift, CheckCircle, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const MS_LABEL = { day1: "First day", day10: "Day 10", day30: "Day 30" };
const STATE_STYLE = {
  paid: "bg-[#2F6B4F]/10 text-[#2F6B4F]",
  due: "bg-[#C0392B]/10 text-[#C0392B] font-semibold",
  waiting_upload: "bg-[#B7791F]/10 text-[#8a5d14]",
  not_started: "bg-stone-100 text-stone-400",
};
const STATE_LABEL = { paid: "Paid", due: "Due", waiting_upload: "Waiting on upload", not_started: "—" };

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/referrals").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pay = async (rid, key) => {
    setBusy(rid + key);
    try {
      await api.post(`/admin/referrals/${rid}/pay/${key}`);
      toast.success("Marked paid");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };
  const close = async (rid) => {
    if (!window.confirm("Close this referral? (No further milestones tracked)")) return;
    try {
      await api.post(`/admin/referrals/${rid}/close`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rows = data?.referrals || [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="referrals-admin-page">
      <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold tracking-tight text-stone-900">
        <Gift size={26} className="text-primary" /> Employee referrals
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-stone-500">
        $50 after the referral's first day worked, $50 at day 10, $50 at day 30. Milestones confirm from the weekly
        visit upload — a bonus shows "due" only once the upload covering that date has landed.
        {data?.visit_coverage && <> Visit data currently covers through <strong>{data.visit_coverage}</strong>.</>}
      </p>

      {rows.length === 0 ? (
        <p className="mt-8 rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-400" data-testid="referrals-empty">
          No referrals yet. Staff submit them from My Profile → "Refer a caregiver".
        </p>
      ) : (
        <div className="mt-6 space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid={`referral-row-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-stone-900">{r.first_name} {r.last_name}
                    <span className="ml-2 text-xs font-normal text-stone-400">referred by {r.referrer_name} · {String(r.created_at).slice(0, 10)}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500">{r.phone || "—"} {r.email && `· ${r.email}`} · status: <span className="font-medium">{r.status}</span></p>
                </div>
                <Button variant="ghost" onClick={() => close(r.id)} className="text-xs text-stone-400 hover:text-[#C0392B]" data-testid={`referral-close-${r.id}`}>
                  <XCircle size={14} className="mr-1" /> Close
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(r.milestones || []).map((m) => (
                  <div key={m.key} className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${STATE_STYLE[m.state] || STATE_STYLE.not_started}`}
                    data-testid={`referral-${r.id}-${m.key}`}>
                    <span>${m.amount} · {m.label}{m.due_on ? ` (${m.due_on})` : ""} — {STATE_LABEL[m.state] || m.state}</span>
                    {m.state === "due" && (
                      <button onClick={() => pay(r.id, m.key)} disabled={busy === r.id + m.key}
                        className="rounded-full bg-[#C0392B] px-2 py-0.5 text-[10px] font-semibold text-white hover:opacity-90"
                        data-testid={`referral-pay-${r.id}-${m.key}`}>
                        <CheckCircle size={11} className="mr-0.5 inline" /> Mark paid
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
